import { computed, reactive } from "@vue/composition-api";
import useMouseSvg from "@/composables/useMouseSvg";
import useSnap from "./useSnap";
import annotations from "@/store/modules/annotations";
import ui from "@/store/modules/ui";

export default function useConnect(nodeRef, edgeRef) {
  const { snap } = useSnap();

  // store states
  const state = reactive({
    nodes: computed(() => annotations.getNodes),
    edges: computed(() => annotations.getEdges),
    snap: computed(() => ui.snap)
  });

  const connect = (e, index) => {
    return new Promise(resolve => {
      // if not left mouse button, return
      if (e.button !== 0) resolve();
      const { pos: mousePosSvg } = useMouseSvg(e);

      annotations.set({ path: `nodes[${index}].selected`, value: true });
      // create handlers for every node
      // ========================================
      // connect to node
      // ----------------------------------------
      const nodeHandlers = [];
      nodeRef.value.forEach(e => {
        const i = parseInt(e.dataset.index);
        nodeHandlers.push(() => {
          // if snap is on, snap self to other node
          if (state.snap) {
            snap(index, i, { line: true });
          }
          // create edge
          annotations.addEdge({ i: index, j: i });

          cleanEvents();
        });
      });

      // create handlers for every edge
      // ----------------------------------------
      // splits edge to make a node, and connect to it
      // o------o
      //
      //    o
      // turns into
      // o--o---o
      //    |
      //    o
      const edgeHandlers = [];
      edgeRef.value.forEach(e => {
        const i = parseInt(e.dataset.index);
        edgeHandlers.push(e => {
          // get mouse position
          const newPt = mousePosSvg(e);

          // create node and connect edge
          const pos = state.edges[i].pos;

          let newY;
          let newX;
          const dx = newPt.x - pos.x1;
          const dy = newPt.y - pos.y1;

          if (Math.abs(dx) > Math.abs(dy)) {
            newY =
              pos.y1 +
              ((newPt.x - pos.x1) * (pos.y2 - pos.y1)) / (pos.x2 - pos.x1);
            newX = newPt.x;
          } else {
            newY = newPt.y;
            newX =
              pos.x1 +
              ((newPt.y - pos.y1) * (pos.x2 - pos.x1)) / (pos.y2 - pos.y1);
          }

          const newNode = {
            selected: false,
            pos: {
              cx: newX,
              cy: newY
            }
          };

          annotations.addNode({ newNode });

          const existingNodes = state.edges[i].nodes;
          const newNodeIndex = state.nodes.length - 1;

          // remove existing edge and create new ones
          annotations.removeDoor({ i: existingNodes[0], j: existingNodes[1] });
          annotations.removeEdge({ i: existingNodes[0], j: existingNodes[1] });
          annotations.addEdge({ i: index, j: newNodeIndex });
          annotations.addEdge({ i: newNodeIndex, j: existingNodes[0] });
          annotations.addEdge({ i: newNodeIndex, j: existingNodes[1] });

          cleanEvents();
        });
      });

      // cleanup event handlers
      const cleanEvents = () => {
        nodeRef.value.forEach((e, i) => {
          e.removeEventListener("mousedown", nodeHandlers[i]);
        });
        edgeRef.value.forEach((e, i) => {
          e.removeEventListener("mousedown", edgeHandlers[i]);
        });

        annotations.set({ path: `nodes[${index}].selected`, value: false });
        resolve();
      };

      // add events
      nodeRef.value.forEach((e, i) => {
        e.addEventListener("mousedown", nodeHandlers[i]);
      });
      edgeRef.value.forEach((e, i) => {
        e.addEventListener("mousedown", edgeHandlers[i]);
      });
    });
  };

  const snapTool = (e, index) => {
    return new Promise(resolve => {
      if (e.button !== 0) resolve();
      // make selected node active
      annotations.set({ path: `nodes[${index}].selected`, value: true });

      // declare handlers
      const nodeHandlers = [];
      nodeRef.value.forEach(e => {
        const i = parseInt(e.dataset.index);
        nodeHandlers.push(() => {
          snap(index, i, { line: true });

          cleanEvents();
        });
      });

      // cleanup event handlers
      const cleanEvents = () => {
        nodeRef.value.forEach((e, i) => {
          e.removeEventListener("mousedown", nodeHandlers[i]);
        });

        annotations.set({ path: `nodes[${index}].selected`, value: false });
        resolve();
      };

      // add events
      nodeRef.value.forEach((e, i) => {
        e.addEventListener("mousedown", nodeHandlers[i]);
      });
    });
  };

  return {
    connect,
    snapTool
  };
}
